// src/pages/analytics/ContentAnalytics.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Button, Card, Chip, useTheme, alpha,
  Breadcrumbs, Link, MenuItem, Select, FormControl, InputLabel,
  FormControlLabel, Switch, Tab, Tabs, CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon, 
  Article as ContentIcon, 
  Visibility as ViewsIcon,
  ThumbUp as EngagementIcon, 
  Create as ContentTypeIcon, 
  Schedule as CalendarIcon,
  NavigateNext as NavigateNextIcon, 
  FileDownload as DownloadIcon, 
  Language as PlatformIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { 
  transformTimeSeriesData,
  transformDistributionData,
  formatNumber,
  generateAnalyticsInsights
} from '../../utils/analyticsUtils';

// Import custom components
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import MetricComparison from '../../components/features/analytics/MetricComparison';
import ExportButton from '../../components/features/analytics/ExportButton';
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';

// Import services
import { exportContentAnalytics } from '../../services/exportService';

// Import responsive styles
import { 
  getKpiGridProps, 
  getChartGridProps, 
  getPageHeaderStyles,
  getTabsContainerStyles,
  getResponsiveChartHeight,
  getFilterBarStyles,
  getMobileCardStyles
} from '../../styles/analyticsResponsive';

/**
 * Content Analytics Page Component - Optimized version
 */
const ContentAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [contentFilter, setContentFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [showTopContent, setShowTopContent] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  const { 
    contentAnalytics, isLoading, error, fetchContentAnalytics,
    dateRange, setDateRange, exportAnalyticsData,
  } = useAnalyticsStore();
  
  const { showError } = useNotificationStore();
  
  // Fetch data when filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchContentAnalytics({
          contentType: contentFilter !== 'all' ? contentFilter : undefined,
          platform: platformFilter !== 'all' ? platformFilter : undefined,
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0],
        });
      } catch (error) {
        showError(`Failed to load content analytics: ${error.message || 'Unknown error'}`);
      }
    };
    
    loadData();
  }, [fetchContentAnalytics, showError, dateRange, contentFilter, platformFilter]);
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main,
    theme.palette.warning.main, theme.palette.error.main, theme.palette.info.main,
  ];
  
  // Check if data is available
  const hasData = !isLoading && contentAnalytics;
  
  // Handlers
  const handleFilterChange = (setter) => (event) => setter(event.target.value);
  const handleTabChange = (_, newValue) => setTabValue(newValue);
  const handleDateRangeChange = (startDate, endDate) => setDateRange(startDate, endDate);
  
  // Handle export analytics
  const handleExportAnalytics = async (format = 'excel') => {
    try {
      const { startDate, endDate } = dateRange;
      
      // Use the export service
      await exportContentAnalytics({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: format,
        contentType: contentFilter !== 'all' ? contentFilter : undefined,
        platform: platformFilter !== 'all' ? platformFilter : undefined,
      });
    } catch (error) {
      showError('Failed to export analytics data: ' + (error.message || 'Unknown error'));
      throw error; // Rethrow to be caught by ExportButton
    }
  };
  
  // State for transformed data
  const [transformedData, setTransformedData] = useState({
    performanceTrends: [],
    contentTypeDistribution: [],
    platformDistribution: [],
    engagementBreakdown: [],
    insights: []
  });
  
  // Format helpers
  const formatName = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  
  // Transform data when contentAnalytics changes
  useEffect(() => {
    if (!contentAnalytics) return;
    
    try {
      // Transform performance trends data
      const performanceTrends = transformTimeSeriesData(
        contentAnalytics.performanceTrends || [],
        {
          dateKey: 'date',
          dateFormat: 'short',
          metrics: ['impressions', 'engagement', 'engagementRate'],
          sortData: true
        }
      );
      
      // Transform content type distribution
      const contentTypeDistribution = transformDistributionData(
        contentAnalytics.contentTypeDistribution || [],
        {
          nameKey: 'type',
          valueKey: 'value',
          sortBy: 'value',
          sortDirection: 'desc',
          calculatePercent: true
        }
      );
      
      // Transform platform distribution
      const platformDistribution = transformDistributionData(
        contentAnalytics.platformDistribution || [],
        {
          nameKey: 'platform',
          valueKey: 'value',
          sortBy: 'value',
          sortDirection: 'desc',
          calculatePercent: true
        }
      );
      
      // Transform engagement breakdown
      const engagementBreakdown = transformDistributionData(
        contentAnalytics.engagementBreakdown || [],
        {
          nameKey: 'type',
          valueKey: 'value',
          sortBy: 'value',
          sortDirection: 'desc',
          calculatePercent: true
        }
      );
      
      // Generate insights
      const insights = generateAnalyticsInsights(
        contentAnalytics.overview || {},
        {
          metrics: ['totalContent', 'totalImpressions', 'totalEngagement', 'engagementRate'],
          comparisonThreshold: 5,
          maxInsights: 3
        }
      );
      
      // Update state with transformed data
      setTransformedData({
        performanceTrends,
        contentTypeDistribution,
        platformDistribution,
        engagementBreakdown,
        insights: insights.insights || []
      });
    } catch (error) {
      console.error('Error transforming analytics data:', error);
    }
  }, [contentAnalytics]);
  
  // Loading state
  if (isLoading && !contentAnalytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link color="inherit" onClick={() => navigate('/analytics')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
            Analytics
          </Link>
          <Typography color="text.primary">Content Analytics</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Content Analytics
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleDateRangeChange}
              size="small"
            />
            
            <ExportButton
              onExport={handleExportAnalytics}
              formats={['excel', 'pdf', 'csv', 'json']}
              buttonText="Export Report"
              size="small"
            />
          </Box>
        </Box>
      </Box>
      
      {/* Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="content-type-filter-label">Content Type</InputLabel>
            <Select
              labelId="content-type-filter-label"
              value={contentFilter}
              label="Content Type"
              onChange={handleFilterChange(setContentFilter)}
              startAdornment={<ContentTypeIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="all">All Content Types</MenuItem>
              <MenuItem value="post">Posts</MenuItem>
              <MenuItem value="story">Stories</MenuItem>
              <MenuItem value="reel">Reels</MenuItem>
              <MenuItem value="carousel">Carousels</MenuItem>
              <MenuItem value="video">Videos</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="platform-filter-label">Platform</InputLabel>
            <Select
              labelId="platform-filter-label"
              value={platformFilter}
              label="Platform"
              onChange={handleFilterChange(setPlatformFilter)}
              startAdornment={<PlatformIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="all">All Platforms</MenuItem>
              <MenuItem value="facebook">Facebook</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
              <MenuItem value="twitter">Twitter</MenuItem>
              <MenuItem value="linkedin">LinkedIn</MenuItem>
              <MenuItem value="tiktok">TikTok</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <FormControlLabel
          control={<Switch checked={showTopContent} onChange={(e) => setShowTopContent(e.target.checked)}  />}
          label="Show Top Content"
        />
      </Box>
      
      {/* Insights */}
      {hasData && transformedData.insights.length > 0 && (
        <Box sx={{ 
          mb: 4, 
          p: 2, 
          borderRadius: 1, 
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Key Insights
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {transformedData.insights.map((insight, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 1,
                  bgcolor: alpha(
                    theme.palette[insight.impact === 'positive' ? 'success' : 'error'].main, 
                    0.05
                  ),
                  border: `1px solid ${alpha(
                    theme.palette[insight.impact === 'positive' ? 'success' : 'error'].main, 
                    0.1
                  )}`,
                  height: '100%'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: theme.palette[insight.impact === 'positive' ? 'success' : 'error'].main,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {insight.impact === 'positive' ? 
                      <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} /> : 
                      <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
                    }
                    {insight.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                    {insight.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      
      {/* Error message */}
      {error && (
        <Card sx={{ p: 2, mb: 4, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}
      
      {/* Overview KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Content"
            value={hasData ? contentAnalytics.overview?.totalContent : null}
            previousValue={hasData ? contentAnalytics.overview?.previousTotalContent : null}
            percentChange={hasData ? contentAnalytics.overview?.totalContentPercentChange : null}
            icon={<ContentIcon />}
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Impressions"
            value={hasData ? contentAnalytics.overview?.totalImpressions : null}
            previousValue={hasData ? contentAnalytics.overview?.previousTotalImpressions : null}
            percentChange={hasData ? contentAnalytics.overview?.impressionsPercentChange : null}
            icon={<ViewsIcon />}
            color="secondary"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Engagement"
            value={hasData ? contentAnalytics.overview?.totalEngagement : null}
            previousValue={hasData ? contentAnalytics.overview?.previousTotalEngagement : null}
            percentChange={hasData ? contentAnalytics.overview?.engagementPercentChange : null}
            icon={<EngagementIcon />}
            color="info"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Engagement Rate"
            value={hasData ? contentAnalytics.overview?.engagementRate : null}
            unit="%"
            previousValue={hasData ? contentAnalytics.overview?.previousEngagementRate : null}
            percentChange={hasData ? contentAnalytics.overview?.engagementRatePercentChange : null}
            icon={<TrendingUpIcon />}
            color="success"
            isLoading={isLoading}
            magical
          />
        </Grid>
      </Grid>
      
      {/* Content tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Performance" />
          <Tab icon={<ContentTypeIcon />} iconPosition="start" label="Content Types" />
          <Tab icon={<PlatformIcon />} iconPosition="start" label="Platforms" />
          <Tab icon={<CalendarIcon />} iconPosition="start" label="Publishing Times" />
        </Tabs>
      </Box>
      
      {/* Performance Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Performance Over Time Chart */}
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Content Performance Trends"
              subtitle="Metrics over time"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              {hasData && transformedData.performanceTrends.length > 0 && (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={transformedData.performanceTrends}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis dataKey="date" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                    <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="impressions" name="Impressions" 
                      stroke={theme.palette.primary.main} activeDot={{ r: 8 }} strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="engagement" name="Engagement" 
                      stroke={theme.palette.secondary.main} strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="engagementRate" name="Engagement Rate (%)" 
                      stroke={theme.palette.success.main} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
          
          {/* Engagement Breakdown */}
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Engagement Breakdown"
              subtitle="Types of engagement"
              icon={<EngagementIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              {hasData && transformedData.engagementBreakdown.length > 0 && (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={transformedData.engagementBreakdown}
                      cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={2}
                      dataKey="value" nameKey="type"
                      label={(entry) => `${entry.type}: ${formatNumber(entry.percent, 'decimal', {digits: 1})}%`}
                      labelLine={false}
                    >
                      {transformedData.engagementBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name, props) => [`${value}%`, props.payload.type]}
                      contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
          
          {/* Top Performing Content */}
          {showTopContent && hasData && contentAnalytics.topContent && (
            <Grid item xs={12}>
              <ChartCard
                title="Top Performing Content"
                subtitle="By engagement rate"
                icon={<ContentIcon />}
                isLoading={isLoading}
                height={500}
                color="info"
              >
                <Box sx={{ height: 450, overflowY: 'auto' }}>
                  <Grid container spacing={2} sx={{ p: 1 }}>
                    {contentAnalytics.topContent.map((content, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <Card sx={{ height: '100%' }}>
                          <Box sx={{ position: 'relative' }}>
                            {content.thumbnail ? (
                              <Box component="img" src={content.thumbnail} alt={content.title}
                                sx={{ width: '100%', height: 180, objectFit: 'cover' }}
                              />
                            ) : (
                              <Box sx={{
                                width: '100%', height: 180, 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}>
                                <ContentIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3) }} />
                              </Box>
                            )}
                            
                            {/* Platform & Type badges */}
                            <Chip label={formatName(content.platform)} size="small"
                              sx={{ position: 'absolute', top: 10, right: 10, bgcolor: alpha(theme.palette.background.paper, 0.8) }}
                            />
                            <Chip label={formatName(content.type)} size="small"
                              sx={{ position: 'absolute', top: 10, left: 10, bgcolor: alpha(theme.palette.background.paper, 0.8) }}
                            />
                          </Box>
                          
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" noWrap title={content.title}>
                              {content.title}
                            </Typography>
                            
                            <Box sx={{ mt: 2 }}>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">Impressions</Typography>
                                  <Typography variant="body2" fontWeight={500}>{content.impressions.toLocaleString()}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">Engagement</Typography>
                                  <Typography variant="body2" fontWeight={500}>{content.engagement.toLocaleString()}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">Engagement Rate</Typography>
                                  <Typography variant="body2" fontWeight={500}>{content.engagementRate}%</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">Date</Typography>
                                  <Typography variant="body2" fontWeight={500}>{new Date(content.date).toLocaleDateString()}</Typography>
                                </Grid>
                              </Grid>
                            </Box>
                            
                            <Button variant="text" size="small" sx={{ mt: 2 }} onClick={() => navigate(`/content/${content.id}`)}>
                              View Content
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </ChartCard>
            </Grid>
          )}
        </Grid>
      )}
      
      {/* Content Types Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* Content Type Distribution */}
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Content Type Distribution"
              subtitle="Breakdown by content type"
              icon={<ContentTypeIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              {hasData && transformedData.contentTypeDistribution.length > 0 && (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={transformedData.contentTypeDistribution}
                      cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={2}
                      dataKey="value" nameKey="type"
                      label={(entry) => `${entry.type}: ${formatNumber(entry.percent, 'decimal', {digits: 1})}%`}
                      labelLine={false}
                    >
                      {transformedData.contentTypeDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name, props) => [`${formatNumber(props.payload.percent, 'decimal', {digits: 1})}%`, props.payload.type]}
                      contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
          
          {/* Content Type Performance Comparison */}
          <Grid item xs={12} sm={6} lg={8}>
            <ChartCard
              title="Content Type Performance"
              subtitle="Comparison by type"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              {hasData && contentAnalytics.contentTypePerformance && (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={contentAnalytics.contentTypePerformance}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis dataKey="type" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                    <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="avgImpressions" name="Avg. Impressions" fill={theme.palette.primary.main} barSize={20} />
                    <Bar yAxisId="left" dataKey="avgEngagement" name="Avg. Engagement" fill={theme.palette.secondary.main} barSize={20} />
                    <Bar yAxisId="right" dataKey="avgEngagementRate" name="Avg. Engagement Rate (%)" fill={theme.palette.success.main} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Platforms Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          {/* Platform Distribution */}
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Platform Distribution"
              subtitle="Content by platform"
              icon={<PlatformIcon />}
              isLoading={isLoading}
              height={400}
              color="info"
              magical
            >
              {hasData && transformedData.platformDistribution.length > 0 && (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={transformedData.platformDistribution}
                      cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={2}
                      dataKey="value" nameKey="platform"
                      label={(entry) => `${entry.platform}: ${formatNumber(entry.percent, 'decimal', {digits: 1})}%`}
                      labelLine={false}
                    >
                      {transformedData.platformDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name, props) => [`${formatNumber(props.payload.percent, 'decimal', {digits: 1})}%`, props.payload.platform]}
                      contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
          
          {/* Platform Performance Comparison */}
          <Grid item xs={12} sm={6} lg={8}>
            <ChartCard
              title="Platform Performance"
              subtitle="Performance metrics by platform"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              {hasData && contentAnalytics.platformPerformance && (
                <ResponsiveContainer width="100%" height={350}>
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={contentAnalytics.platformPerformance}>
                    <PolarGrid stroke={alpha(theme.palette.divider, 0.3)} />
                    <PolarAngleAxis dataKey="platform" tick={{ fill: theme.palette.text.secondary }} />
                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fill: theme.palette.text.secondary }} />
                    <Radar name="Impressions" dataKey="impressions" 
                      stroke={theme.palette.primary.main} 
                      fill={alpha(theme.palette.primary.main, 0.2)} fillOpacity={0.6} />
                    <Radar name="Engagement" dataKey="engagement" 
                      stroke={theme.palette.secondary.main} 
                      fill={alpha(theme.palette.secondary.main, 0.2)} fillOpacity={0.6} />
                    <Radar name="Engagement Rate" dataKey="engagementRate" 
                      stroke={theme.palette.success.main} 
                      fill={alpha(theme.palette.success.main, 0.2)} fillOpacity={0.6} />
                    <Legend />
                    <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                  </RadarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Publishing Times Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard
              title="Best Publishing Times"
              subtitle="Performance by day and time"
              icon={<CalendarIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              {hasData && contentAnalytics.publishingTimes ? (
                <Box sx={{ p: 2, height: 350 }}>
                  <Typography variant="body1" align="center">Publishing times data visualization would go here</Typography>
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 2 }}>
                    This would typically be a heatmap showing engagement rates by day of week and time of day
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2, height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="textSecondary">No publishing times data available</Typography>
                </Box>
              )}
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ContentAnalytics;